// src/components/PrivateRoute/index.tsx
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import TokenManager from '../../utils/tokenManager';
import { LOGIN } from '@/pages/routes';
import RouteFallback from '@/components/RouteFallback';
import {
  ContentFallback,
  useInShell
} from '@/components/AppShell/ContentFallback';

/**
 * Ultra-simple protected route
 * - No context needed
 * - No need to call checkAuth() anywhere
 * - Just works automatically
 */
const ProtectedRoute = () => {
  const location = useLocation();
  const inShell = useInShell();
  const [authState, setAuthState] = useState<
    'checking' | 'authenticated' | 'unauthenticated'
  >('checking');

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      // ✅ Simple: Just check if token exists
      const hasToken = await TokenManager.hasToken();

      if (mounted) {
        setAuthState(hasToken ? 'authenticated' : 'unauthenticated');
      }
    };

    checkAuth();

    return () => {
      mounted = false;
    };
  }, []);

  // Still checking: the splash on a cold start, the page's skeleton after.
  // The skeleton shows before the verdict, so a signed-out user is redirected
  // from it rather than after a blank screen.
  if (authState === 'checking') {
    // Inside the shared frame the header is real already: only the body waits.
    return inShell ? <ContentFallback /> : <RouteFallback />;
  }

  // Not authenticated - redirect to login
  if (authState === 'unauthenticated') {
    // Login sends the user back here once they are signed in.
    return (
      <Navigate
        to={LOGIN}
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  // Authenticated - render content
  return <Outlet />;
};

export default ProtectedRoute;
